// The look of the app (docs/UI_THEME.md), pure: what a choice means, what is kept where, what the <html> element carries. The root view (resources/views/app.blade.php) holds
// the same rule as an inline script so that the first paint is right; tests/js/themeLogic.test.ts keeps the two in step.

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly ThemePreference[] = [
	'system',
	'light',
	'dark',
];
export const THEME_STORAGE_KEY = 'uni-theme';
export const THEME_COOKIE = 'uni_theme';
const COOKIE_DAYS = 365;

export const isThemePreference = (value: unknown): value is ThemePreference =>
	typeof value === 'string' &&
	(THEME_PREFERENCES as readonly string[]).includes(value);

/** What a choice looks like right now: "system" follows the browser's preference. */
export const resolveTheme = (
	preference: ThemePreference,
	systemPrefersDark: boolean,
): ResolvedTheme =>
	preference === 'dark' || (preference === 'system' && systemPrefersDark)
		? 'dark'
		: 'light';

/**
 * The choice that holds when the page starts: the person's own (the server), else what this browser kept (storage, then the cookie), else the installation's default. Anything that
 * is not one of the three is ignored, never trusted.
 */
export const startingPreference = (input: {
	server?: unknown;
	stored?: unknown;
	cookie?: unknown;
	fallback?: unknown;
}): ThemePreference => {
	for (const candidate of [
		input.server,
		input.stored,
		input.cookie,
		input.fallback,
	]) {
		if (isThemePreference(candidate)) return candidate;
	}

	return 'system';
};

/** The value of the theme cookie inside a `document.cookie` string. */
export const cookiePreference = (cookies: string): ThemePreference | null => {
	const match = /(?:^|; )uni_theme=(system|light|dark)/.exec(cookies);

	return match ? (match[1] as ThemePreference) : null;
};

/** The cookie that mirrors the choice (readable by the server-rendered first paint of the next visit). */
export const themeCookie = (preference: ThemePreference): string =>
	`${THEME_COOKIE}=${preference}; path=/; max-age=${COOKIE_DAYS * 86400}; SameSite=Lax`;

/** What <html> carries for a resolved theme: the `dark` class (Tailwind's variant), the attributes the light rules and the tests read, and the native colour scheme. */
export const rootState = (
	preference: ThemePreference,
	resolved: ResolvedTheme,
): {
	dark: boolean;
	theme: ResolvedTheme;
	pref: ThemePreference;
	colorScheme: ResolvedTheme;
} => ({
	dark: resolved === 'dark',
	theme: resolved,
	pref: preference,
	colorScheme: resolved,
});

/** The label of a choice in the menu. */
export const themeLabel = (preference: ThemePreference): string =>
	preference === 'system'
		? 'System'
		: preference === 'light'
			? 'Light'
			: 'Dark';
